import type { Locale } from '@/stores/language'

/** The BCP 47 tag behind each app locale, for `Intl` and `toLocale*String`. */
export const LOCALE_TO_BCP47: Record<Locale, string> = {
  enUs: 'en-US',
  ptBr: 'pt-BR',
  esEs: 'es-ES',
  frFr: 'fr-FR',
  deDe: 'de-DE',
  itIt: 'it-IT',
}

/**
 * The app locale for a code in either casing, or `undefined` when the value
 * isn't one of ours.
 *
 * Locale codes reach us PascalCase off the wire (`PtBr`) and camelCase from
 * i18next (`ptBr`), and callers read them from storage and the URL too — so
 * the input is genuinely an arbitrary string and a miss has to stay visible.
 */
export function toAppLocale(
  locale: string | undefined | null,
): Locale | undefined {
  if (!locale) return undefined

  const candidate = locale.charAt(0).toLowerCase() + locale.slice(1)
  // `hasOwn`, not `in` — `in` walks the prototype, so "constructor" would pass.
  return Object.hasOwn(LOCALE_TO_BCP47, candidate)
    ? (candidate as Locale)
    : undefined
}

/** The BCP 47 tag for an app locale code, or `undefined` if it isn't one. */
export function toBcp47(locale: string | undefined | null): string | undefined {
  const appLocale = toAppLocale(locale)
  return appLocale ? LOCALE_TO_BCP47[appLocale] : undefined
}

/**
 * Normalises whatever locale string the caller has into BCP 47 for `Intl.*`.
 * Values that are already BCP 47 (`pt-BR`) fall through untouched.
 */
export function normalizeLocaleForIntl(
  locale: string | undefined | null,
): string {
  if (!locale) return 'en-US'
  return toBcp47(locale) ?? locale
}
